import React from "react";

import { Users, Download } from "lucide-react";

import { ButtonShadcn } from "@/components/ui/Button";
import DataTable from "@/components/shared/DataTable";
import Skeleton from "@/components/shared/Skeleton";
import { ExportButtons, Section, EmptyNote, Collapsible, LowSampleBadge, CHART, fmtDays } from "./primitives";
import { getInitials as initials } from "@/utils/formatText";

// F3 — reviewer activity & workload. Data's job: rank a set of entities by a
// magnitude AND show each one's composition. The right form is a ranked TABLE
// with an inline throughput bar per row (magnitude) plus a small action-mix
// stacked bar (composition) — not another standalone chart. Reviewers are
// discovered from the audit trail; the tenant roll-up is the baseline row
// (DL-2B-4: no team entity, no per-reviewer backlog here).

const MIX = [
  { key: "approved", label: "Approved", color: CHART.green },
  { key: "rejected", label: "Rejected", color: CHART.red },
  { key: "reRequested", label: "Re-requested", color: CHART.violet },
  { key: "fieldsConfirmed", label: "Fields confirmed", color: CHART.blue },
];

const ActionMixBar = ({ row }) => {
  const total = row.totalActions || 0;
  if (!total) return <div className="h-2.5 w-full rounded bg-background-tertiary" />;
  return (
    <div className="flex h-2.5 w-full rounded overflow-hidden" title="Action mix">
      {MIX.map((m) => (row[m.key] > 0 ? <div key={m.key} style={{ width: `${(row[m.key] / total) * 100}%`, background: m.color }} /> : null))}
    </div>
  );
};

const ReviewersSection = ({ data, loading, onExportCsv, onExportPdf, exporting }) => {
  const reviewers = data?.reviewers ?? [];
  const team = data?.teamRollup;
  const minSampleSize = data?.minSampleSize ?? 10;
  const maxActions = Math.max(1, ...reviewers.map((r) => r.totalActions || 0));
  const columns = [
    {
      key: "name",
      label: "Reviewer",
      render: (_, reviewer) => (
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white"
            style={{ background: reviewer.belowMinSample ? CHART.slate : CHART.indigo }}
          >
            {initials(reviewer.name)}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[12px] leading-tight text-text-primary">{reviewer.name}</p>
            {reviewer.email ? <p className="truncate text-[10px] leading-tight text-text-secondary">{reviewer.email}</p> : null}
          </div>
        </div>
      ),
    },
    {
      key: "totalActions",
      label: "Throughput",
      className: "w-[24%]",
      render: (_, reviewer) => (
        <div className="flex items-center gap-2">
          <div className="h-4 flex-1 overflow-hidden rounded bg-background-tertiary">
            <div
              className="h-full rounded"
              style={{
                width: `${((reviewer.totalActions || 0) / maxActions) * 100}%`,
                background: `${reviewer.belowMinSample ? CHART.slate : CHART.indigo}cc`,
              }}
            />
          </div>
          <span className="w-7 text-right text-[12px] font-semibold tabular-nums text-text-primary">{reviewer.totalActions}</span>
        </div>
      ),
    },
    {
      key: "actionMix",
      label: "Action mix",
      className: "w-[20%]",
      render: (_, reviewer) => <ActionMixBar row={reviewer} />,
    },
    {
      key: "medianTimeToReviewDays",
      label: "Median TTR",
      align: "right",
      render: (value) => fmtDays(value),
    },
    {
      key: "p90TimeToReviewDays",
      label: "p90 TTR",
      align: "right",
      render: (value) => fmtDays(value),
    },
  ];

  return (
    <Section
      icon={Users}
      title="Reviewer activity & workload"
      subtitle="How is the team working — who is carrying the load, who is struggling?"
      action={<ExportButtons onExportCsv={onExportCsv} onExportPdf={onExportPdf} loading={loading} busy={exporting} />}
    >
      {loading ? (
        <div className="flex flex-col gap-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : reviewers.length === 0 ? (
        <EmptyNote>No documents were actioned in this period, so there is no reviewer activity to show. (Reviewers appear here once they act on a document.)</EmptyNote>
      ) : (
        <>
          {/* Team roll-up — the headline baseline, expanded by default */}
          {team ? (
            <div className="rounded-xl p-3.5 bg-background-tertiary border border-border-primary flex flex-wrap items-center gap-x-6 gap-y-2">
              <div>
                <p className="text-[11px] text-text-secondary">Team (tenant total)</p>
                <p className="text-[1.1rem] font-bold text-text-primary tabular-nums">{team.totalActions} actions</p>
              </div>
              <div className="flex-1 min-w-[140px] max-w-[220px]">
                <ActionMixBar row={team} />
              </div>
              <div>
                <p className="text-[10px] text-text-secondary">Median TTR</p>
                <p className="text-[12px] font-semibold text-text-primary tabular-nums">{fmtDays(team.medianTimeToReviewDays)}</p>
              </div>
              <div>
                <p className="text-[10px] text-text-secondary">p90 TTR</p>
                <p className="text-[12px] font-semibold text-text-primary tabular-nums">{fmtDays(team.p90TimeToReviewDays)}</p>
              </div>
            </div>
          ) : null}

          {/* Per-reviewer ranked table — the detail breakdown, collapsed by default */}
          <Collapsible title={`Per-reviewer breakdown (${reviewers.length})`}>
            <DataTable
              className="[&_table]:min-w-[760px]"
              columns={columns}
              apiData={reviewers}
              rowKey="userId"
              sortable={false}
              showPagination={false}
              pageSize={reviewers.length}
            />
            <p className="text-[10px] text-text-secondary mt-2">
              Rows marked "low n" (fewer than {minSampleSize} actions) are shown but excluded from outlier comparisons and the attention strip — a reviewer with 2-3
              actions shouldn't read as a real outlier.
            </p>
          </Collapsible>
        </>
      )}

      {/* mix legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
        {MIX.map((m) => (
          <span key={m.key} className="flex items-center gap-1 text-[10px] text-text-secondary">
            <span className="w-2 h-2 rounded-sm" style={{ background: m.color }} />
            {m.label}
          </span>
        ))}
        <span className="text-[10px] text-text-secondary">· TTR = time-to-review</span>
      </div>
    </Section>
  );
};

export default ReviewersSection;
